"use client";

import Intercom, { shutdown } from "@intercom/messenger-js-sdk";

/**
 * Clear of the apply funnel's mobile alias bar, which sits at `bottom-4` and
 * is 44px tall. Matches the lift the expert-agent widget takes.
 */
const CORNER_SHARED_PADDING_PX = 76;
import { env } from "@repo/env";
import { useEffect } from "react";

import { useSession } from "@/lib/auth-client";
import { api } from "@/lib/trpc/react";

export function IntercomWidget({
  sharesTheCorner = false,
}: {
  /**
   * Same meaning as on the expert-agent widget, and lifted by the same amount.
   * If only one arm moved out of the funnel's way, the comparison between them
   * would be measuring which launcher was easier to reach.
   */
  sharesTheCorner?: boolean;
} = {}) {
  const appId = env.NEXT_PUBLIC_INTERCOM_APP_ID;
  const { data: session } = useSession();
  const user = session?.user;

  // Sensitive identity attributes are signed in the JWT; never trust the
  // client to assert who it is. The query is gated on having an authenticated
  // user; signed-out visitors boot anonymously below.
  const { data: jwtData } = api.intercom.getUserJwt.useQuery(undefined, {
    enabled: Boolean(appId && user?.id),
    // Refresh well before the 1h server-side TTL.
    staleTime: 1000 * 60 * 30,
    refetchInterval: 1000 * 60 * 30,
  });
  const intercomUserJwt = jwtData?.token;

  useEffect(() => {
    if (!appId) {
      return;
    }
    // If a user is authenticated, wait for the JWT before booting; Intercom
    // will reject identified pings without it once IV is enforced.
    if (user?.id && !intercomUserJwt) {
      return;
    }
    Intercom({
      app_id: appId,
      ...(intercomUserJwt ? { intercom_user_jwt: intercomUserJwt } : {}),
      // Its launcher is injected outside React, so it cannot be moved with a
      // class: this is Intercom's own way of saying the same thing.
      ...(sharesTheCorner
        ? { vertical_padding: CORNER_SHARED_PADDING_PX }
        : {}),
    });

    // Intercom injects a global launcher that survives client-side navigation.
    // Without this teardown the widget leaks onto every page reached after
    // leaving the /me layout (e.g. the org-scoped study creation flow).
    return () => {
      shutdown();
    };
  }, [appId, user?.id, intercomUserJwt, sharesTheCorner]);

  return null;
}
